import {
  cacheExchange,
  ClientOptions,
  createClient,
  fetchExchange,
  mapExchange,
  SSRExchange,
  ssrExchange,
} from 'urql';
import { createFetch } from '@rea-argonaut/fetch';
import { getEnv } from '../helpers/getEnv';
import { read, write } from '@rea-argonaut/exchange';
import { logger } from '@rea-argonaut/observability';
import { devtoolsExchange } from '@urql/devtools';

export const URQL_CACHE_KEY = 'URQL_CACHE';

let urqlSsrExchange: SSRExchange;
export const getSsrExchange = async () => {
  const isClient = process.env.WEBPACK_TARGET === 'web';

  if (!urqlSsrExchange) {
    urqlSsrExchange = ssrExchange({
      isClient,
      initialState: (await read(URQL_CACHE_KEY)) || undefined, // Read urql cache from exchange
    });
  }

  return urqlSsrExchange;
};

export const writeUrqlCacheToExchange = async () => {
  await write(async () => {
    const ssrExchange = await getSsrExchange();
    return ssrExchange.extractData();
  }, URQL_CACHE_KEY);
};

export const createUrqlClientConfig = async (): Promise<ClientOptions> => {
  const isClient = process.env.WEBPACK_TARGET === 'web';
  const experienceApiUrl = isClient
    ? getEnv.PUBLIC_EXPERIENCE_API_URL()
    : getEnv.INTERNAL_EXPERIENCE_API_URL();
  const url = `${experienceApiUrl}/api/graphql`;

  return {
    url,
    fetch: createFetch({}),
    fetchOptions: {
      credentials: 'include',
    },
    // @urql/core v6 sends queries as GET by default; keep POST as the Experience API expects
    preferGetMethod: false,
    exchanges: [
      devtoolsExchange,
      cacheExchange,
      mapExchange({
        onError(error, operation) {
          logger.error({
            error,
            description: `urql operation ${operation.key} has errored with: ${error.name}`,
            customAttributes: {
              errorName: error.name,
              errorMessage: error.message,
              operation: JSON.stringify(operation),
            },
          });
        },
      }),
      await getSsrExchange(),
      fetchExchange,
    ],
  };
};

let urqlClient: ReturnType<typeof createClient>;
export const createUrqlClient = async () => {
  if (!urqlClient) {
    urqlClient = createClient(await createUrqlClientConfig());
  }
  return urqlClient;
};
